import type { ChildProcess } from "node:child_process";
import { copyFileSync, type Dirent, linkSync, mkdirSync, readdirSync, readFileSync, rmSync, statSync } from "node:fs";
import { homedir, arch as osArch, platform as osPlatform } from "node:os";
import { delimiter, dirname, join, relative } from "node:path";
import type { Readable } from "node:stream";
import { pipeline } from "node:stream/promises";
import * as nodeZlib from "node:zlib";
import { StreamingLineDecoder } from "@caupulican/pi-ai/streaming-lines";
import { getBundledResourcesDir } from "../../config.ts";
import {
	type ChildProcessTerminationResult,
	spawnProcess,
	waitForChildProcessWithTermination,
} from "../../utils/child-process.ts";
import { modelsDir as agentModelsDir, runtimesDir as agentRuntimesDir } from "../agent-paths.ts";
import {
	deriveHostLocalInferenceProfile,
	type LocalInferenceProfile,
	type LocalInferenceProfileMode,
	ollamaEnvironmentForLocalInferenceProfile,
} from "./local-inference-profile.ts";
import {
	createRuntimeCommandRunner,
	extractZipArchive,
	installRuntimeArchive,
	type ManagedRuntimeSpawn,
	type RuntimeCommandResult,
	type RuntimeCommandRunner,
	requireRuntimeStdin,
	resolveRuntimeLifecycleDependencies,
	runtimeCommandAvailable,
} from "./runtime-process.ts";

export type { RuntimeCommandResult, RuntimeCommandRunner } from "./runtime-process.ts";

const MAX_OLLAMA_PULL_LINE_CHARS = 64 * 1024 * 1024;

/**
 * Local model runtime manager (local-model-lifecycle-design.md): Ollama first, interface kept
 * runtime-agnostic. Pi SPAWNS the serve process itself with OWNED model storage
 * (`OLLAMA_MODELS=<agentDir>/models/ollama`) so every downloaded weight lives inside pi's tree —
 * per-model disk accounting is trivial and full cleanup is one directory. User-level Ollama stores
 * can be imported into the owned store, but are never silently served or deleted.
 *
 * Hard boundaries (design "Hard boundaries"): lifecycle actions are USER commands only — this
 * module is never exposed as a model-invokable tool; install is GUIDE MODE (exact manual steps,
 * never `curl | sh`); removal is explicit, disclosed, and never automatic.
 */

export type OllamaStoreKind = "pi-owned" | "user" | "external";

export interface OllamaStoreSummary {
	kind: OllamaStoreKind;
	path: string;
	modelCount: number;
}

export interface LocalRuntimeStatus {
	binaryPath?: string;
	binarySource?: "system" | "user" | "pi-owned";
	serverUp: boolean;
	serverUrl: string;
	/** True when the responding server is a child process pi spawned (owned storage applies). */
	managedByPi: boolean;
	/** Owned weights directory. This is pi's canonical Ollama store. */
	ownedModelsDir: string;
	userModelsDir: string;
	ownedStore: OllamaStoreSummary;
	userStore: OllamaStoreSummary;
	activeStore?: OllamaStoreSummary;
	/** Models reported by the configured live server; empty when it is down. */
	serverModels: InstalledLocalModel[];
}

export interface InstalledLocalModel {
	name: string;
	sizeBytes: number;
}

interface OllamaPsModel {
	name?: string;
	model?: string;
	size?: number;
	size_vram?: number;
}

export interface OllamaImportResult {
	sourceDir: string;
	targetDir: string;
	manifestsImported: number;
	manifestsSkipped: number;
	blobsHardlinked: number;
	blobsCopied: number;
	blobsSkipped: number;
	bytesImported: number;
}

export interface OllamaModelInfo {
	modelInfo: Record<string, unknown>;
}

export type OllamaModelParameter = string | number | boolean;

/** Current structured `/api/create` contract. Keep raw Modelfile syntax out of callers so managed
 * profiles and context sizing cannot drift from the runtime adapter's supported request shape. */
export interface OllamaCreateModelInput {
	name: string;
	from: string;
	template?: string;
	renderer?: string;
	parser?: string;
	parameters?: Readonly<Record<string, OllamaModelParameter>>;
}

/** Pinned ollama release for the managed installer below. Bump here when needed — one constant. */
export const OLLAMA_PINNED_VERSION = "0.31.1";

export type OllamaAssetKind = "tar-zst" | "tar-gz" | "zip";

export interface OllamaReleaseAsset {
	name: string;
	kind: OllamaAssetKind;
}

/**
 * Maps a platform/arch pair (node's `os.platform()`/`os.arch()`) to the exact ollama release asset
 * name for {@link OLLAMA_PINNED_VERSION} — verified against the real ollama/ollama GitHub release
 * and its own install.sh, not guessed: darwin ships a CLI `.tgz` (the `.app`/`.dmg`/`Ollama-*.zip`
 * assets are the separate GUI-app installer, not what we want), linux ships `.tar.zst` per arch,
 * windows ships `.zip` per arch. Pure and exported so it's independently testable and reusable
 * (e.g. by a future doctor-driven managed install) without touching OllamaRuntime.
 */
export function resolveOllamaAsset(plat: string, architecture: string): OllamaReleaseAsset | undefined {
	if (plat === "darwin") {
		// Universal binary — one asset for both arm64 and x64.
		return { name: "ollama-darwin.tgz", kind: "tar-gz" };
	}
	if (plat === "linux") {
		const archName = architecture === "arm64" ? "arm64" : "amd64";
		return { name: `ollama-linux-${archName}.tar.zst`, kind: "tar-zst" };
	}
	if (plat === "win32") {
		const archName = architecture === "arm64" ? "arm64" : "amd64";
		return { name: `ollama-windows-${archName}.zip`, kind: "zip" };
	}
	return undefined;
}

export interface LocalRuntimeDeps {
	fetchFn?: typeof fetch;
	spawnFn?: ManagedRuntimeSpawn;
	existsFn?: (path: string) => boolean;
	linkFile?: (source: string, target: string) => void;
	copyFile?: (source: string, target: string) => void;
	envPath?: string;
	homeDir?: string;
	sleepFn?: (ms: number) => Promise<void>;
	/** os.platform()/os.arch() equivalents — injectable so asset/runtime resolution is testable per platform. */
	platform?: () => string;
	arch?: () => string;
	/** Host capacity probes used once to derive the bounded local inference profile. */
	totalMemoryBytes?: () => number;
	logicalCpuCount?: () => number;
	/** Runs a runtime-management command (Python venv/pip/download probe). Injectable so tests never
	 * install packages or hit the network. */
	runCommand?: RuntimeCommandRunner;
	/** Override for bundled runtime helper scripts, used by tests and source/binary packaging checks. */
	transformersServerScriptPath?: string;
	/** Node's built-in zstd decompress transform, when THIS runtime's node:zlib has it — undefined
	 * forces the system-zstd fallback. Injectable so tests can force either path deterministically
	 * instead of depending on whatever Node happens to be running the test. */
	createZstdDecompress?: () => NodeJS.ReadWriteStream;
	/** Whether a named command exists on PATH (e.g. system `zstd`), for the extraction fallback. */
	hasCommand?: (command: string) => boolean;
	/** Runs the extraction step for a downloaded archive. Injectable so installManaged's
	 * download->extract->verify orchestration is testable without a real tar/zstd/unzip pipeline;
	 * defaults to the real spawn-based extractor. */
	extractArchive?: (
		input: NodeJS.ReadableStream,
		destDir: string,
		kind: OllamaAssetKind,
	) => Promise<{ ok: boolean; error?: string }>;
}

const DEFAULT_BASE_URL = "http://127.0.0.1:11434";
const HEALTH_TIMEOUT_MS = 2_000;
const LOCAL_RUNTIME_COMMAND_TIMEOUT_MS = 15 * 60_000;
const LOCAL_RUNTIME_EXTRACTION_TIMEOUT_MS = 10 * 60_000;
const LOCAL_RUNTIME_KILL_GRACE_MS = 2_000;
const START_POLL_ATTEMPTS = 20;
const START_POLL_INTERVAL_MS = 500;
const TRANSFORMERS_PORT_BASE = 18_100;
const TRANSFORMERS_PORT_SPAN = 1_000;
const TRANSFORMERS_START_POLL_ATTEMPTS = 240;
const TRANSFORMERS_RUNTIME_DIR_NAME = "hf-transformers";
const TRANSFORMERS_VENV_DIR_NAME = "venv";
const TRANSFORMERS_SERVER_RELATIVE_PATH = join("runtimes", "hf-transformers-openai-server.py");
const TRANSFORMERS_PINNED_PACKAGES = ["transformers==5.13.0", "huggingface-hub==1.22.0", "safetensors==0.8.0"];
const TRANSFORMERS_REQUIRED_MODULES = ["torch", "transformers", "huggingface_hub", "safetensors"];
const TORCH_PINNED_VERSION = "2.12.1";

function terminateManagedRuntimeProcess(child: ChildProcess): void {
	const terminationController = new AbortController();
	terminationController.abort();
	void waitForChildProcessWithTermination(child, {
		signal: terminationController.signal,
		killGraceMs: LOCAL_RUNTIME_KILL_GRACE_MS,
	}).catch(() => {
		child.kill("SIGKILL");
		child.unref();
	});
}

function isHardlinkFallbackError(error: unknown): boolean {
	if (typeof error !== "object" || error === null) return false;
	const code = (error as { code?: unknown }).code;
	return code === "EXDEV" || code === "EACCES" || code === "EPERM";
}

function fileSizeBytes(path: string): number {
	try {
		return statSync(path).size;
	} catch {
		return 0;
	}
}

const runCommandDefault = createRuntimeCommandRunner({
	timeoutMs: LOCAL_RUNTIME_COMMAND_TIMEOUT_MS,
	killGraceMs: LOCAL_RUNTIME_KILL_GRACE_MS,
});

function sanitizeCommandOutput(value: string | undefined): string {
	return value?.trim().split("\n").slice(-3).join("\n") || "unknown error";
}

function hashString(value: string): number {
	let hash = 2_166_136_261;
	for (let index = 0; index < value.length; index++) {
		hash ^= value.charCodeAt(index);
		hash = Math.imul(hash, 16_777_619) >>> 0;
	}
	return hash;
}

export function resolveTransformersBaseUrl(modelId: string): string {
	const port = TRANSFORMERS_PORT_BASE + (hashString(modelId) % TRANSFORMERS_PORT_SPAN);
	return `http://127.0.0.1:${port}`;
}

export class OllamaRuntime {
	private readonly _agentDir: string;
	private readonly _baseUrl: string;
	private readonly _fetch: typeof fetch;
	private readonly _spawn: NonNullable<LocalRuntimeDeps["spawnFn"]>;
	private readonly _exists: (path: string) => boolean;
	private readonly _linkFile: (source: string, target: string) => void;
	private readonly _copyFile: (source: string, target: string) => void;
	private readonly _envPath: string;
	private readonly _homeDir: string;
	private readonly _sleep: (ms: number) => Promise<void>;
	private readonly _platform: () => string;
	private readonly _arch: () => string;
	private readonly _createZstdDecompress: (() => NodeJS.ReadWriteStream) | undefined;
	private readonly _hasCommand: (command: string) => boolean;
	private readonly _extractArchiveFn: NonNullable<LocalRuntimeDeps["extractArchive"]>;
	private readonly _profile: LocalInferenceProfile;
	private _child: Pick<ChildProcess, "pid" | "kill" | "unref" | "on"> | undefined;
	private _childModelsDir: string | undefined;

	constructor(args: {
		agentDir: string;
		baseUrl?: string;
		profileMode?: LocalInferenceProfileMode;
		deps?: LocalRuntimeDeps;
	}) {
		this._agentDir = args.agentDir;
		this._baseUrl = (args.baseUrl ?? DEFAULT_BASE_URL).replace(/\/$/, "");
		[this._fetch, this._spawn, this._exists, this._sleep] = resolveRuntimeLifecycleDependencies(args.deps);
		this._linkFile = args.deps?.linkFile ?? linkSync;
		this._copyFile = args.deps?.copyFile ?? copyFileSync;
		this._envPath = args.deps?.envPath ?? process.env.PATH ?? "";
		this._homeDir = args.deps?.homeDir ?? homedir();
		this._platform = args.deps?.platform ?? osPlatform;
		this._arch = args.deps?.arch ?? osArch;
		this._profile = deriveHostLocalInferenceProfile(args.profileMode ?? "balanced", args.deps);
		// Feature-detect (not version-sniff): recent Node added zstd natively to node:zlib. Verified
		// present on this repo's minimum supported Node (>=22.19.0); still detected at runtime rather
		// than assumed, so an older/different Node correctly falls through to the system-zstd path.
		// `"createZstdDecompress" in deps` (rather than `??`) so a test can force the fallback path by
		// passing the key as explicitly undefined, distinct from omitting it (real auto-detection).
		this._createZstdDecompress =
			args.deps && "createZstdDecompress" in args.deps
				? args.deps.createZstdDecompress
				: typeof nodeZlib.createZstdDecompress === "function"
					? () => nodeZlib.createZstdDecompress()
					: undefined;
		this._hasCommand = args.deps?.hasCommand ?? runtimeCommandAvailable;
		this._extractArchiveFn =
			args.deps?.extractArchive ?? ((input, destDir, kind) => this._extractArchive(input, destDir, kind));
	}

	get baseUrl(): string {
		return this._baseUrl;
	}

	ownedModelsDir(): string {
		return agentModelsDir("ollama", this._agentDir);
	}

	userModelsDir(): string {
		return join(this._homeDir, ".ollama", "models");
	}

	private _storeSummary(kind: OllamaStoreKind, path: string): OllamaStoreSummary {
		return { kind, path, modelCount: this._countStoreModels(path) };
	}

	private _countStoreModels(storeDir: string): number {
		return this._walkFiles(join(storeDir, "manifests")).length;
	}

	private _walkFiles(root: string): string[] {
		if (!this._exists(root)) return [];
		let entries: Dirent[];
		try {
			entries = readdirSync(root, { withFileTypes: true });
		} catch {
			return [];
		}
		const files: string[] = [];
		for (const entry of entries) {
			const path = join(root, entry.name);
			if (entry.isDirectory()) files.push(...this._walkFiles(path));
			else if (entry.isFile()) files.push(path);
		}
		return files;
	}

	private _findBinary(): { path: string; source: "system" | "user" | "pi-owned" } | undefined {
		const binaryName = this._platform() === "win32" ? "ollama.exe" : "ollama";
		const piOwned = join(agentRuntimesDir("ollama", this._agentDir), "bin", binaryName);
		if (this._exists(piOwned)) return { path: piOwned, source: "pi-owned" };
		const userLevel = join(this._homeDir, ".local", "share", "ollama-dist", "bin", binaryName);
		if (this._exists(userLevel)) return { path: userLevel, source: "user" };
		for (const dir of this._envPath.split(delimiter)) {
			if (!dir) continue;
			const candidate = join(dir, binaryName);
			if (this._exists(candidate)) return { path: candidate, source: "system" };
		}
		return undefined;
	}

	private async _serverUp(): Promise<boolean> {
		return (await this._serverModels()).up;
	}

	private async _serverModels(): Promise<{ up: boolean; models: InstalledLocalModel[] }> {
		try {
			const response = await this._fetch(`${this._baseUrl}/api/tags`, {
				signal: AbortSignal.timeout(HEALTH_TIMEOUT_MS),
			});
			if (!response.ok) return { up: false, models: [] };
			const data = (await response.json().catch(() => ({}))) as {
				models?: Array<{ name?: unknown; size?: unknown }>;
			};
			const models = Array.isArray(data.models)
				? data.models
						.filter((model): model is { name: string; size?: number } => typeof model.name === "string")
						.map((model) => ({ name: model.name, sizeBytes: typeof model.size === "number" ? model.size : 0 }))
				: [];
			return { up: true, models };
		} catch {
			return { up: false, models: [] };
		}
	}

	private _activeStoreSummary(
		serverModelCount: number,
		ownedStore: OllamaStoreSummary,
		userStore: OllamaStoreSummary,
	): OllamaStoreSummary {
		if (this._childModelsDir) {
			return {
				kind: this._childModelsDir === this.ownedModelsDir() ? "pi-owned" : "user",
				path: this._childModelsDir,
				modelCount: serverModelCount,
			};
		}
		if (
			serverModelCount > 0 &&
			serverModelCount === ownedStore.modelCount &&
			serverModelCount !== userStore.modelCount
		) {
			return { ...ownedStore, modelCount: serverModelCount };
		}
		if (
			serverModelCount > 0 &&
			serverModelCount === userStore.modelCount &&
			serverModelCount !== ownedStore.modelCount
		) {
			return { ...userStore, modelCount: serverModelCount };
		}
		return { kind: "external", path: "external/unknown", modelCount: serverModelCount };
	}

	async detect(): Promise<LocalRuntimeStatus> {
		const binary = this._findBinary();
		const serverModels = await this._serverModels();
		const ownedStore = this._storeSummary("pi-owned", this.ownedModelsDir());
		const userStore = this._storeSummary("user", this.userModelsDir());
		const activeStore = serverModels.up
			? this._activeStoreSummary(serverModels.models.length, ownedStore, userStore)
			: undefined;
		return {
			binaryPath: binary?.path,
			binarySource: binary?.source,
			serverUp: serverModels.up,
			serverUrl: this._baseUrl,
			managedByPi: this._child !== undefined && serverModels.up,
			ownedModelsDir: this.ownedModelsDir(),
			userModelsDir: this.userModelsDir(),
			ownedStore,
			userStore,
			activeStore,
			serverModels: serverModels.models,
		};
	}

	importUserModels(): OllamaImportResult {
		const sourceDir = this.userModelsDir();
		const targetDir = this.ownedModelsDir();
		const result: OllamaImportResult = {
			sourceDir,
			targetDir,
			manifestsImported: 0,
			manifestsSkipped: 0,
			blobsHardlinked: 0,
			blobsCopied: 0,
			blobsSkipped: 0,
			bytesImported: 0,
		};
		if (sourceDir === targetDir || !this._exists(sourceDir)) return result;
		mkdirSync(targetDir, { recursive: true });
		this._importManifestFiles(join(sourceDir, "manifests"), join(targetDir, "manifests"), result);
		this._importBlobFiles(join(sourceDir, "blobs"), join(targetDir, "blobs"), result);
		return result;
	}

	private _importManifestFiles(sourceRoot: string, targetRoot: string, result: OllamaImportResult): void {
		for (const source of this._walkFiles(sourceRoot)) {
			const target = join(targetRoot, relative(sourceRoot, source));
			if (this._exists(target)) {
				result.manifestsSkipped++;
				continue;
			}
			mkdirSync(dirname(target), { recursive: true });
			this._copyFile(source, target);
			result.manifestsImported++;
		}
	}

	private _importBlobFiles(sourceRoot: string, targetRoot: string, result: OllamaImportResult): void {
		for (const source of this._walkFiles(sourceRoot)) {
			const target = join(targetRoot, relative(sourceRoot, source));
			if (this._exists(target)) {
				result.blobsSkipped++;
				continue;
			}
			mkdirSync(dirname(target), { recursive: true });
			try {
				this._linkFile(source, target);
				result.blobsHardlinked++;
			} catch (error) {
				if (!isHardlinkFallbackError(error)) throw error;
				this._copyFile(source, target);
				result.blobsCopied++;
			}
			result.bytesImported += fileSizeBytes(target);
		}
	}

	/**
	 * Manual-steps fallback text — shown when the managed install below wasn't offered (headless),
	 * was declined, or itself failed. #31 reversed the stance for ollama specifically: pi CAN install
	 * it for you now (consent-gated, a controlled download into pi's own runtimes dir, never
	 * `curl | sh`) — these steps are the alternative for when that path isn't taken.
	 */
	installGuide(): string[] {
		return [
			"Ollama is not installed. Pi can install it for you now (asks first, never curl|sh) — or follow these manual steps yourself:",
			"  1. Download the pinned release archive for your platform:",
			"     https://github.com/ollama/ollama/releases (asset: ollama-linux-amd64.tar.zst or your platform)",
			`  2. Extract it to ${join(this._homeDir, ".local", "share", "ollama-dist")}`,
			"  3. Re-run your /models command - pi detects the binary automatically.",
			"Alternatively install system-wide from https://ollama.com/download and pi will use the system server.",
		];
	}

	/** Which decompressor to use for a `.tar.zst` asset — native (this Node's own zlib) is always
	 * preferred when available, even if a system `zstd` ALSO exists, since it needs no external
	 * dependency at all. Verified per-runtime by feature detection (see the constructor), not by
	 * guessing from a Node version number. */
	private _chooseZstdStrategy(): { kind: "native" } | { kind: "system" } | { kind: "unavailable" } {
		if (this._createZstdDecompress) return { kind: "native" };
		if (this._hasCommand("zstd")) return { kind: "system" };
		return { kind: "unavailable" };
	}

	/**
	 * Managed install (#31): a consent-gated, controlled download of the pinned ollama release into
	 * pi's OWN runtimes dir (`<agentDir>/runtimes/ollama/`) — never `curl | sh`, never a second
	 * server instance, and distinct from {@link start}'s owned MODEL storage (this is the binary
	 * itself). Callers (the router's consent flow, and later a doctor-driven install) own the
	 * consent/UI; this method only does the mechanical download+extract+verify and reports the
	 * outcome honestly. `onProgress` is best-effort UI feedback, not a completion signal.
	 */
	async installManaged(onProgress?: (status: string) => void): Promise<{ ok: boolean; error?: string }> {
		const asset = resolveOllamaAsset(this._platform(), this._arch());
		if (!asset) return { ok: false, error: "unsupported-platform" };

		const downloadUrl = `https://github.com/ollama/ollama/releases/download/v${OLLAMA_PINNED_VERSION}/${asset.name}`;
		const extracted = await this._installArchive(downloadUrl, asset, onProgress);
		if (!extracted.ok) return extracted;

		const destDir = agentRuntimesDir("ollama", this._agentDir);
		const binaryName = this._platform() === "win32" ? "ollama.exe" : "ollama";
		const binaryPath = join(destDir, "bin", binaryName);
		if (!this._exists(binaryPath)) {
			return { ok: false, error: "extract-fail: binary not found after extraction" };
		}
		onProgress?.("Ollama installed.");
		return { ok: true };
	}

	private _installArchive(
		downloadUrl: string,
		asset: OllamaReleaseAsset,
		onProgress?: (status: string) => void,
	): Promise<{ ok: boolean; error?: string }> {
		return installRuntimeArchive(
			this._fetch,
			downloadUrl,
			agentRuntimesDir("ollama", this._agentDir),
			asset,
			this._extractArchiveFn,
			onProgress,
		);
	}

	/** Real extraction: `tar-gz` lets `tar` itself gunzip; `tar-zst` decompresses first (native
	 * node:zlib preferred, system `zstd` as fallback, an honest error if neither exists — see
	 * {@link _chooseZstdStrategy}); `zip` needs a seekable file, so it's buffered to disk first. */
	private async _extractArchive(
		input: NodeJS.ReadableStream,
		destDir: string,
		kind: OllamaAssetKind,
	): Promise<{ ok: boolean; error?: string }> {
		try {
			if (kind === "zip") {
				return await this._extractZip(input, destDir);
			}
			return await this._extractTar(input, destDir, kind);
		} catch (error) {
			return { ok: false, error: `extract-fail: ${error instanceof Error ? error.message : String(error)}` };
		}
	}

	private async _extractTar(
		input: NodeJS.ReadableStream,
		destDir: string,
		kind: "tar-gz" | "tar-zst",
	): Promise<{ ok: boolean; error?: string }> {
		const tarArgs = ["-xf", "-", "-C", destDir];
		let nativeZstd: (NodeJS.ReadWriteStream & Readable) | undefined;
		let zstdProc: ChildProcess | undefined;
		if (kind === "tar-zst") {
			const strategy = this._chooseZstdStrategy();
			if (strategy.kind === "unavailable") {
				return {
					ok: false,
					error: "zstd-missing: this archive needs zstd to decompress and neither Node's built-in support nor a system `zstd` binary was found. Install zstd (e.g. `apt-get install zstd`, `dnf install zstd`, `pacman -S zstd`) and try again.",
				};
			}
			if (strategy.kind === "native") {
				nativeZstd = this._createZstdDecompress?.() as unknown as NodeJS.ReadWriteStream & Readable;
			} else {
				zstdProc = spawnProcess("zstd", ["-d"], {
					detached: process.platform !== "win32",
					stdio: ["pipe", "pipe", "ignore"],
				});
			}
		} else {
			// tar itself handles gzip via -z — no separate decompression step needed.
			tarArgs[0] = "-xzf";
		}
		const tarProc = spawnProcess("tar", tarArgs, {
			detached: process.platform !== "win32",
			stdio: ["pipe", "ignore", "ignore"],
		});
		const terminationController = new AbortController();
		const processWaits = [tarProc, ...(zstdProc ? [zstdProc] : [])].map((proc) =>
			waitForChildProcessWithTermination(proc, {
				signal: terminationController.signal,
				timeoutMs: LOCAL_RUNTIME_EXTRACTION_TIMEOUT_MS,
				killGraceMs: LOCAL_RUNTIME_KILL_GRACE_MS,
			}),
		);
		const streamPipelines = nativeZstd
			? [pipeline(input as unknown as Readable, nativeZstd, requireRuntimeStdin(tarProc, "tar"))]
			: zstdProc
				? [
						pipeline(input as unknown as Readable, requireRuntimeStdin(zstdProc, "zstd")),
						pipeline(zstdProc.stdout as unknown as Readable, requireRuntimeStdin(tarProc, "tar")),
					]
				: [pipeline(input as unknown as Readable, requireRuntimeStdin(tarProc, "tar"))];
		let terminals: ChildProcessTerminationResult[];
		try {
			[, terminals] = await Promise.all([Promise.all(streamPipelines), Promise.all(processWaits)]);
		} catch (error) {
			terminationController.abort();
			await Promise.allSettled(processWaits);
			throw error;
		}
		const failedProcess = terminals.find((terminal) => terminal.reason === "timeout" || terminal.code !== 0);
		if (failedProcess) {
			return {
				ok: false,
				error:
					failedProcess.reason === "timeout"
						? `extract-fail: archive extraction timed out after ${LOCAL_RUNTIME_EXTRACTION_TIMEOUT_MS}ms`
						: `extract-fail: archive extraction exited with code ${failedProcess.code ?? "unknown"}`,
			};
		}
		return { ok: true };
	}

	private async _extractZip(input: NodeJS.ReadableStream, destDir: string): Promise<{ ok: boolean; error?: string }> {
		return extractZipArchive({
			input: input as unknown as Readable,
			destDir,
			tempPrefix: "ollama-download",
			platform: this._platform,
			hasCommand: this._hasCommand,
			timeoutMs: LOCAL_RUNTIME_EXTRACTION_TIMEOUT_MS,
			killGraceMs: LOCAL_RUNTIME_KILL_GRACE_MS,
		});
	}

	/** Shared spawn-then-health-poll for both start modes below; `extraEnv` is the only thing that
	 * differs between them (owned storage vs reusing the user's own). */
	private async _spawnAndPoll(
		binary: { path: string },
		extraEnv: NodeJS.ProcessEnv,
	): Promise<{ started: boolean; reason: string }> {
		const host = this._baseUrl.replace(/^https?:\/\//, "");
		const env: NodeJS.ProcessEnv = { ...process.env, OLLAMA_HOST: host, ...extraEnv };
		this._childModelsDir = env.OLLAMA_MODELS ?? this.userModelsDir();
		this._child = this._spawn(binary.path, ["serve"], {
			detached: process.platform !== "win32",
			env,
			stdio: "ignore",
		});
		this._child.unref?.();
		for (let attempt = 0; attempt < START_POLL_ATTEMPTS; attempt++) {
			if (await this._serverUp()) return { started: true, reason: "started" };
			await this._sleep(START_POLL_INTERVAL_MS);
		}
		this.stop();
		return { started: false, reason: "health_check_timeout" };
	}

	private async _startServer(modelsDir?: string): Promise<{ started: boolean; reason: string }> {
		if (await this._serverUp()) {
			return { started: false, reason: this._child ? "already_running_managed" : "already_running_system" };
		}
		const binary = this._findBinary();
		if (!binary) return { started: false, reason: "binary_missing" };
		if (modelsDir) mkdirSync(modelsDir, { recursive: true });
		return this._spawnAndPoll(binary, {
			...(modelsDir ? { OLLAMA_MODELS: modelsDir } : {}),
			...ollamaEnvironmentForLocalInferenceProfile(this._profile),
		});
	}

	/**
	 * Start a pi-managed serve with OWNED storage and the hardened env verified on this class of
	 * hardware. No-op (reported) when a server already responds — pi never double-serves. Used by
	 * `/models add` et al, where isolated per-model-pull storage is the point.
	 */
	async start(): Promise<{ started: boolean; reason: string }> {
		return this._startServer(this.ownedModelsDir());
	}

	/** Start against one caller-selected store; acceptance harnesses use this on an isolated port. */
	async startWithModelsStore(modelsDir: string): Promise<{ started: boolean; reason: string }> {
		return this._startServer(modelsDir);
	}

	/**
	 * Start a serve that REUSES the user's own existing models directory (no `OLLAMA_MODELS`
	 * override — falls through to Ollama's own default, `~/.ollama`), for callers that must see the
	 * user's already-pulled models rather than pi's isolated/owned storage. Same idempotency and
	 * hardened perf env as {@link start}; the only difference is which storage the server sees.
	 */
	async startReuseExisting(): Promise<{ started: boolean; reason: string }> {
		return this._startServer();
	}

	/** Resource hygiene only: stops the pi-managed serve process; never deletes anything. */
	stop(): { stopped: boolean } {
		const child = this._child;
		if (!child) return { stopped: false };
		this._child = undefined;
		this._childModelsDir = undefined;
		terminateManagedRuntimeProcess(child as ChildProcess);
		return { stopped: true };
	}

	async list(): Promise<InstalledLocalModel[]> {
		const response = await this._fetch(`${this._baseUrl}/api/tags`, { signal: AbortSignal.timeout(10_000) });
		if (!response.ok) throw new Error(`ollama list failed: HTTP ${response.status}`);
		const data = (await response.json()) as { models?: Array<{ name?: string; size?: number }> };
		return (data.models ?? [])
			.filter((model): model is { name: string; size?: number } => typeof model.name === "string")
			.map((model) => ({ name: model.name, sizeBytes: model.size ?? 0 }));
	}

	async listResidentModels(): Promise<InstalledLocalModel[]> {
		const response = await this._fetch(`${this._baseUrl}/api/ps`, { signal: AbortSignal.timeout(10_000) });
		if (!response.ok) throw new Error(`ollama ps failed: HTTP ${response.status}`);
		const data = (await response.json()) as { models?: OllamaPsModel[] };
		return (data.models ?? [])
			.map((model) => ({ name: model.name ?? model.model, sizeBytes: model.size_vram ?? model.size ?? 0 }))
			.filter((model): model is InstalledLocalModel => typeof model.name === "string" && model.name.length > 0);
	}

	async ensureResident(model: string): Promise<{ ok: boolean; error?: string }> {
		return this._postKeepAlive(model, this._profile.keepAlive);
	}

	async releaseResident(model: string): Promise<{ ok: boolean; error?: string }> {
		return this._postKeepAlive(model, 0);
	}

	private async _postKeepAlive(model: string, keepAlive: string | number): Promise<{ ok: boolean; error?: string }> {
		try {
			const response = await this._fetch(`${this._baseUrl}/api/generate`, {
				method: "POST",
				headers: { "content-type": "application/json" },
				body: JSON.stringify({
					model,
					prompt: "",
					stream: false,
					keep_alive: keepAlive,
					options: { num_predict: 0 },
				}),
				signal: AbortSignal.timeout(60_000),
			});
			if (!response.ok)
				return { ok: false, error: `HTTP ${response.status}: ${await response.text().catch(() => "")}` };
			return { ok: true };
		} catch (error) {
			return { ok: false, error: error instanceof Error ? error.message : String(error) };
		}
	}

	async show(ref: string): Promise<{ ok: true; info: OllamaModelInfo } | { ok: false; error: string }> {
		try {
			const response = await this._fetch(`${this._baseUrl}/api/show`, {
				method: "POST",
				headers: { "content-type": "application/json" },
				body: JSON.stringify({ model: ref }),
				signal: AbortSignal.timeout(10_000),
			});
			if (!response.ok) {
				return {
					ok: false,
					error: `show failed: HTTP ${response.status} ${await response.text().catch(() => "")}`,
				};
			}
			const data = (await response.json()) as { model_info?: Record<string, unknown> };
			return { ok: true, info: { modelInfo: data.model_info ?? {} } };
		} catch (error) {
			return { ok: false, error: error instanceof Error ? error.message : String(error) };
		}
	}

	private async _requestModelMutation(
		endpoint: string,
		init: RequestInit,
		operation: string,
	): Promise<{ ok: true } | { ok: false; error: string }> {
		try {
			const response = await this._fetch(`${this._baseUrl}${endpoint}`, init);
			if (!response.ok) {
				return {
					ok: false,
					error: `${operation} failed: HTTP ${response.status} ${await response.text().catch(() => "")}`,
				};
			}
			return { ok: true };
		} catch (error) {
			return { ok: false, error: error instanceof Error ? error.message : String(error) };
		}
	}

	async createModel(args: OllamaCreateModelInput): Promise<{ ok: true } | { ok: false; error: string }> {
		const { name, ...definition } = args;
		return this._requestModelMutation(
			"/api/create",
			{
				method: "POST",
				headers: { "content-type": "application/json" },
				body: JSON.stringify({ model: name, ...definition, stream: false }),
				signal: AbortSignal.timeout(60_000),
			},
			"create",
		);
	}

	/** Pull a model through the server API (weights land in the SERVER's models dir). */
	async pull(ref: string, onProgress?: (status: string) => void): Promise<{ ok: boolean; error?: string }> {
		try {
			const response = await this._fetch(`${this._baseUrl}/api/pull`, {
				method: "POST",
				headers: { "content-type": "application/json" },
				body: JSON.stringify({ model: ref, stream: true }),
			});
			if (!response.ok || !response.body) {
				return {
					ok: false,
					error: `pull failed: HTTP ${response.status} ${await response.text().catch(() => "")}`,
				};
			}
			const reader = response.body.getReader();
			const decoder = new TextDecoder();
			const lines = new StreamingLineDecoder(MAX_OLLAMA_PULL_LINE_CHARS, { lineEndings: "lf" });
			let lastStatus = "";
			let errorMessage: string | undefined;
			const handleLine = (line: string): void => {
				if (!line.trim()) return;
				try {
					const event = JSON.parse(line) as { status?: string; error?: string };
					if (event.error) errorMessage = event.error;
					if (event.status && event.status !== lastStatus) {
						lastStatus = event.status;
						onProgress?.(event.status);
					}
				} catch {
					// partial line noise
				}
			};
			for (;;) {
				const { done, value } = await reader.read();
				if (done) break;
				for (const line of lines.push(decoder.decode(value, { stream: true }))) handleLine(line);
			}
			for (const line of lines.push(decoder.decode())) handleLine(line);
			// The stream's final line may have no trailing newline and still carries the terminal event.
			const finalLine = lines.finish();
			if (finalLine !== undefined) handleLine(finalLine);
			if (errorMessage) return { ok: false, error: errorMessage };
			return lastStatus === "success"
				? { ok: true }
				: { ok: false, error: `pull ended with: ${lastStatus || "unknown"}` };
		} catch (error) {
			return { ok: false, error: error instanceof Error ? error.message : String(error) };
		}
	}

	/** EXPLICIT user action only — callers must have shown what gets deleted and confirmed. */
	async remove(ref: string): Promise<{ ok: boolean; error?: string }> {
		return this._requestModelMutation(
			"/api/delete",
			{
				method: "DELETE",
				headers: { "content-type": "application/json" },
				body: JSON.stringify({ model: ref }),
			},
			"delete",
		);
	}
}

export interface TransformersRuntimeStatus {
	runtimeInstalled: boolean;
	serverUp: boolean;
	baseUrl: string;
	modelId: string;
	venvDir: string;
	cacheDir: string;
	serverScriptPath: string;
}

export class TransformersRuntime {
	private readonly _agentDir: string;
	private readonly _modelId: string;
	private readonly _baseUrl: string;
	private readonly _fetch: typeof fetch;
	private readonly _spawn: NonNullable<LocalRuntimeDeps["spawnFn"]>;
	private readonly _exists: (path: string) => boolean;
	private readonly _sleep: (ms: number) => Promise<void>;
	private readonly _platform: () => string;
	private readonly _runCommand: RuntimeCommandRunner;
	private readonly _serverScriptPath: string;
	private _proc?: Pick<ChildProcess, "pid" | "kill" | "unref" | "on">;

	constructor(args: { agentDir: string; modelId: string; baseUrl?: string; deps?: LocalRuntimeDeps }) {
		this._agentDir = args.agentDir;
		this._modelId = args.modelId;
		this._baseUrl = args.baseUrl?.replace(/\/$/, "") ?? resolveTransformersBaseUrl(args.modelId);
		[this._fetch, this._spawn, this._exists, this._sleep] = resolveRuntimeLifecycleDependencies(args.deps);
		this._platform = args.deps?.platform ?? osPlatform;
		this._runCommand = args.deps?.runCommand ?? runCommandDefault;
		this._serverScriptPath =
			args.deps?.transformersServerScriptPath ?? join(getBundledResourcesDir(), TRANSFORMERS_SERVER_RELATIVE_PATH);
	}

	get baseUrl(): string {
		return this._baseUrl;
	}

	get modelId(): string {
		return this._modelId;
	}

	get runtimeDir(): string {
		return agentRuntimesDir(TRANSFORMERS_RUNTIME_DIR_NAME, this._agentDir);
	}

	get venvDir(): string {
		return join(this.runtimeDir, TRANSFORMERS_VENV_DIR_NAME);
	}

	get cacheDir(): string {
		return agentModelsDir("huggingface", this._agentDir);
	}

	get pythonPath(): string {
		return this._platform() === "win32"
			? join(this.venvDir, "Scripts", "python.exe")
			: join(this.venvDir, "bin", "python");
	}

	private get serverPort(): string {
		const parsed = new URL(this._baseUrl);
		return parsed.port || (parsed.protocol === "https:" ? "443" : "80");
	}

	private get serverHost(): string {
		return new URL(this._baseUrl).hostname;
	}

	private runtimeEnv(): NodeJS.ProcessEnv {
		const venvBin = this._platform() === "win32" ? join(this.venvDir, "Scripts") : join(this.venvDir, "bin");
		return {
			...process.env,
			VIRTUAL_ENV: this.venvDir,
			PATH: `${venvBin}${delimiter}${process.env.PATH ?? ""}`,
			PYTHONNOUSERSITE: "1",
			HF_HOME: this.cacheDir,
			HUGGINGFACE_HUB_CACHE: join(this.cacheDir, "hub"),
			TRANSFORMERS_CACHE: join(this.cacheDir, "transformers"),
			HF_HUB_DISABLE_TELEMETRY: "1",
		};
	}

	private async serverUp(): Promise<boolean> {
		try {
			const response = await this._fetch(`${this._baseUrl}/health`, {
				signal: AbortSignal.timeout(HEALTH_TIMEOUT_MS),
			});
			if (!response.ok) return false;
			const body = (await response.json().catch(() => undefined)) as { model?: unknown } | undefined;
			return body?.model === this._modelId;
		} catch {
			return false;
		}
	}

	async detect(): Promise<TransformersRuntimeStatus> {
		const serverUp = await this.serverUp();
		return {
			runtimeInstalled: this._exists(this.pythonPath) && (serverUp || (await this.runtimeDependenciesInstalled())),
			serverUp,
			baseUrl: this._baseUrl,
			modelId: this._modelId,
			venvDir: this.venvDir,
			cacheDir: this.cacheDir,
			serverScriptPath: this._serverScriptPath,
		};
	}

	private pythonCandidates(): Array<{ command: string; args: string[] }> {
		const envPython = process.env.PYTHON?.trim();
		const envCandidates = envPython ? [{ command: envPython, args: [] }] : [];
		return this._platform() === "win32"
			? [...envCandidates, { command: "py", args: ["-3"] }, { command: "python", args: [] }]
			: [...envCandidates, { command: "python", args: [] }, { command: "python3", args: [] }];
	}

	private async findPython(): Promise<{ command: string; args: string[] } | undefined> {
		for (const candidate of this.pythonCandidates()) {
			const result = await this._runCommand(candidate.command, [...candidate.args, "--version"]);
			if (result.ok) return candidate;
		}
		return undefined;
	}

	private torchInstallArgs(): string[] {
		if (this._platform() === "darwin") {
			return ["-m", "pip", "install", `torch==${TORCH_PINNED_VERSION}`];
		}
		return [
			"-m",
			"pip",
			"install",
			`torch==${TORCH_PINNED_VERSION}+cpu`,
			"--index-url",
			"https://download.pytorch.org/whl/cpu",
		];
	}

	private async runPython(args: string[], onProgress?: (status: string) => void): Promise<RuntimeCommandResult> {
		return this._runCommand(this.pythonPath, args, {
			env: this.runtimeEnv(),
			onOutput: onProgress
				? (chunk) => {
						const line = chunk.trim();
						if (line) onProgress(line);
					}
				: undefined,
		});
	}

	private async runtimeDependenciesInstalled(): Promise<boolean> {
		if (!this._exists(this.pythonPath)) return false;
		const modules = JSON.stringify(TRANSFORMERS_REQUIRED_MODULES);
		const result = await this.runPython([
			"-c",
			`import importlib.util, sys; missing = [name for name in ${modules} if importlib.util.find_spec(name) is None]; sys.exit(1 if missing else 0)`,
		]);
		return result.ok;
	}

	private readPyvenvHome(): string | undefined {
		const pyvenvCfg = join(this.venvDir, "pyvenv.cfg");
		if (!this._exists(pyvenvCfg)) return undefined;
		try {
			const text = readFileSync(pyvenvCfg, "utf8");
			const homeLine = text
				.split(/\r?\n/)
				.map((line) => line.trim())
				.find((line) => line.toLowerCase().startsWith("home ="));
			return homeLine?.slice(homeLine.indexOf("=") + 1).trim() || undefined;
		} catch {
			return undefined;
		}
	}

	private async venvInterpreterCoherent(): Promise<boolean> {
		const pyvenvHome = this.readPyvenvHome();
		if (!pyvenvHome) return false;
		const base = await this.runPython(["-c", "import sys; print(sys.base_prefix or sys.prefix)"]);
		if (!base.ok) return false;
		const basePrefix = base.stdout.trim();
		if (!basePrefix) return true;
		return pyvenvHome === basePrefix || pyvenvHome.startsWith(basePrefix) || basePrefix.startsWith(pyvenvHome);
	}

	private async venvHealthy(): Promise<boolean> {
		const pip = await this.runPython(["-m", "pip", "--version"]);
		return pip.ok && (await this.venvInterpreterCoherent());
	}

	private linuxVenvPackageCommand(): string {
		try {
			const osRelease = readFileSync("/etc/os-release", "utf8").toLowerCase();
			if (osRelease.includes("alpine")) return "sudo apk add python3 py3-pip";
			if (osRelease.includes("fedora") || osRelease.includes("rhel") || osRelease.includes("centos")) {
				return "sudo dnf install python3";
			}
		} catch {
			// fall through to the most common Debian/Ubuntu package split.
		}
		return "sudo apt install python3-venv";
	}

	private venvInstallHint(): string {
		if (this._platform() === "darwin") return "install Python from python.org or run: brew install python";
		if (this._platform() === "win32") return "install Python 3 from python.org, then run: py -3 -m venv <path>";
		if (this._platform() === "linux") return this.linuxVenvPackageCommand();
		return "install Python with venv/ensurepip support for this platform";
	}

	private venvFailure(error: string | undefined): { ok: false; error: string } {
		return {
			ok: false,
			error: `venv-fail: ${sanitizeCommandOutput(error)}. Install Python venv support: ${this.venvInstallHint()}`,
		};
	}

	private async createVenv(onProgress?: (status: string) => void): Promise<{ ok: boolean; error?: string }> {
		const python = await this.findPython();
		if (!python) return { ok: false, error: "python-missing" };
		onProgress?.(`creating isolated Python venv at ${this.venvDir}`);
		const venv = await this._runCommand(python.command, [...python.args, "-m", "venv", this.venvDir]);
		if (!venv.ok) return this.venvFailure(venv.error ?? venv.stderr);
		if (!this._exists(this.pythonPath)) return this.venvFailure(`${this.pythonPath} was not created`);
		return { ok: true };
	}

	private async repairExistingVenv(onProgress?: (status: string) => void): Promise<{ ok: boolean; error?: string }> {
		if (await this.venvHealthy()) return { ok: true };
		onProgress?.("repairing pi-managed Transformers venv with ensurepip");
		const ensurepip = await this.runPython(["-m", "ensurepip", "--upgrade"], onProgress);
		if (ensurepip.ok && (await this.venvHealthy())) return { ok: true };
		onProgress?.("recreating pi-managed Transformers venv");
		rmSync(this.venvDir, { recursive: true, force: true });
		return this.createVenv(onProgress);
	}

	private async ensurePipAvailable(onProgress?: (status: string) => void): Promise<{ ok: boolean; error?: string }> {
		const pip = await this.runPython(["-m", "pip", "--version"]);
		if (pip.ok) return { ok: true };
		onProgress?.("bootstrapping pip inside pi-managed venv");
		const ensurepip = await this.runPython(["-m", "ensurepip", "--upgrade"], onProgress);
		if (!ensurepip.ok) return this.venvFailure(ensurepip.error ?? ensurepip.stderr);
		const verified = await this.runPython(["-m", "pip", "--version"]);
		return verified.ok ? { ok: true } : this.venvFailure(verified.error ?? verified.stderr);
	}

	async installManaged(onProgress?: (status: string) => void): Promise<{ ok: boolean; error?: string }> {
		mkdirSync(this.runtimeDir, { recursive: true });
		mkdirSync(this.cacheDir, { recursive: true });
		if (this._exists(this.pythonPath)) {
			const repaired = await this.repairExistingVenv(onProgress);
			if (!repaired.ok) return repaired;
		}
		if (!this._exists(this.pythonPath)) {
			const created = await this.createVenv(onProgress);
			if (!created.ok) return created;
		}
		const pipReady = await this.ensurePipAvailable(onProgress);
		if (!pipReady.ok) return pipReady;

		onProgress?.("upgrading pip inside pi-managed venv");
		const pip = await this.runPython(["-m", "pip", "install", "--upgrade", "pip"], onProgress);
		if (!pip.ok) return { ok: false, error: `pip-upgrade-fail: ${sanitizeCommandOutput(pip.error ?? pip.stderr)}` };

		onProgress?.("installing pinned Transformers runtime packages");
		const transformers = await this.runPython(["-m", "pip", "install", ...TRANSFORMERS_PINNED_PACKAGES], onProgress);
		if (!transformers.ok) {
			return {
				ok: false,
				error: `transformers-install-fail: ${sanitizeCommandOutput(transformers.error ?? transformers.stderr)}`,
			};
		}

		onProgress?.("installing pinned CPU PyTorch backend");
		const torch = await this.runPython(this.torchInstallArgs(), onProgress);
		if (!torch.ok)
			return { ok: false, error: `torch-install-fail: ${sanitizeCommandOutput(torch.error ?? torch.stderr)}` };

		const verify = await this.runPython([
			"-c",
			"import torch, transformers, huggingface_hub; print(transformers.__version__)",
		]);
		if (!verify.ok)
			return { ok: false, error: `verify-fail: ${sanitizeCommandOutput(verify.error ?? verify.stderr)}` };
		return { ok: true };
	}

	async downloadModel(onProgress?: (status: string) => void): Promise<{ ok: boolean; error?: string }> {
		if (!this._exists(this.pythonPath)) return { ok: false, error: "runtime-missing" };
		if (!(await this.runtimeDependenciesInstalled())) return { ok: false, error: "runtime-dependencies-missing" };
		if (!this._exists(this._serverScriptPath))
			return { ok: false, error: `server-script-missing: ${this._serverScriptPath}` };
		mkdirSync(this.cacheDir, { recursive: true });
		onProgress?.(`downloading ${this._modelId} into ${this.cacheDir}`);
		const result = await this.runPython(
			[this._serverScriptPath, "--model-id", this._modelId, "--cache-dir", this.cacheDir, "--download-only"],
			onProgress,
		);
		return result.ok
			? { ok: true }
			: { ok: false, error: `download-fail: ${sanitizeCommandOutput(result.error ?? result.stderr)}` };
	}

	async start(): Promise<{ started: boolean; reason: string }> {
		if (await this.serverUp()) return { started: false, reason: "already_running" };
		if (!this._exists(this.pythonPath)) return { started: false, reason: "runtime_missing" };
		if (!this._exists(this._serverScriptPath)) return { started: false, reason: "server_script_missing" };
		mkdirSync(this.cacheDir, { recursive: true });
		const env = this.runtimeEnv();
		this._proc = this._spawn(
			this.pythonPath,
			[
				this._serverScriptPath,
				"--model-id",
				this._modelId,
				"--host",
				this.serverHost,
				"--port",
				this.serverPort,
				"--cache-dir",
				this.cacheDir,
			],
			{ detached: process.platform !== "win32", env, stdio: "ignore" },
		);
		this._proc.unref?.();
		for (let attempt = 0; attempt < TRANSFORMERS_START_POLL_ATTEMPTS; attempt++) {
			if (await this.serverUp()) return { started: true, reason: "started" };
			await this._sleep(START_POLL_INTERVAL_MS);
		}
		this.stop();
		return { started: false, reason: "health_check_timeout" };
	}

	stop(): { stopped: boolean } {
		const child = this._proc;
		if (!child) return { stopped: false };
		this._proc = undefined;
		terminateManagedRuntimeProcess(child as ChildProcess);
		return { stopped: true };
	}

	installGuide(): string[] {
		return [
			"Hugging Face Transformers runtime is not installed for this model.",
			`Pi can install it into its own venv at ${this.venvDir} and cache weights under ${this.cacheDir}.`,
			"Manual equivalent:",
			`  python -m venv ${this.venvDir}`,
			`  ${this.pythonPath} -m pip install --upgrade pip`,
			`  ${this.pythonPath} -m pip install ${TRANSFORMERS_PINNED_PACKAGES.join(" ")}`,
			`  ${this.pythonPath} ${this.torchInstallArgs().join(" ")}`,
		];
	}
}
